import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { setSearchText } from "../../../redux/historicalSlice";
import { exportAuditLogsToCSV, useGetAuditLogs } from "../api/auditApi";
import { useAlert } from "../../../hooks/useAlert";
import { Button, Col, Row } from "react-bootstrap";
import AlertMessage from "../../../components/AlertMessage";
import Search from "../../../components/Search";
import { AuditActionEnum, AuditEntityEnum, AuditLogListItem } from "../types";
import AppTable, { ColumnConfig } from "../../../components/AppTable/AppTable";
import { UseSelectBasicObject } from "../../../types/UseSelectBasicObject";
import ModalGlobal from "../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../hooks/useModal";
import SpinerLoader from "../../../components/SpinerLoader";
import FiltersContainer from "../../../components/Filters/components/FiltersContainer";
import { FilterDto, FilterType } from "../../../types/FilterDto";
import { RootState } from "../../../redux/store";
import { PaginationFilterDto } from "../../../types/Pagination";
import { formatToLocalTime } from "../../../utils/formatters/DateFormatter";
import { SectionPermissionTypeEnum } from "../../../types/SectionPermissionTypesEnum";

export default function AuditLogsList() {
    const { t } = useTranslation(['common', 'auditLog']);

    const dispatch = useDispatch();
    const selectedFilters = useSelector((state: RootState) => state.filters.auditLogs.selectedFilters);
    const pagination = useSelector((state: RootState) => state.historical.auditLogs);

    const [searchTerm, setSearchTerm] = useState('');
    const [showCsvModal, setShowCsvModal] = useState<boolean>(false);    
    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const [messageError, setMessageError] = useState<string | undefined>('');

    const { errorModal, setErrorModal, setLoading, isLoading } = useModal();
    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });
    const { data: tableData, isLoading: isLoadingData } = useGetAuditLogs();
   
    const columns: ColumnConfig<AuditLogListItem>[] = [
        { header: t("auditLog:tableHeader.date"), key: "timestamp", render: (item) => formatToLocalTime(item.timestamp) },
        { header: t("auditLog:tableHeader.userId"), key: "userId" },
        { header: t("auditLog:tableHeader.username"), key: "userName" },
        { header: t("auditLog:tableHeader.entity"), key: "entity", render: (item) => t(`auditLog:entity.${item.entity}`) },
        { header: t("auditLog:tableHeader.action"), key: "action", render: (item) => t(`auditLog:actionType.${item.action}`) },
        { header: t("auditLog:tableHeader.recordId"), key: "recordId" }
    ];

    const filters: FilterDto[] = [
        {
            name: 'Action',
            description: t('auditLog:tableHeader.action'),
            type: FilterType.MULTIPLE,
            values: Object.keys(AuditActionEnum)
                .filter((key) => isNaN(Number(key)))
                .map((value) => ({
                    key: AuditActionEnum[value as keyof typeof AuditActionEnum],
                    value: t(`auditLog:actionType.${value}`)
                }))
                .sort((a, b) => a.value.localeCompare(b.value))
        },
        {
            name: 'Controller', // Nombre de la columna a filtrar en back
            description: t('auditLog:tableHeader.entity'),
            type: FilterType.MULTIPLE,
            values: Object.keys(AuditEntityEnum)
                .filter((key) => isNaN(Number(key)))
                .map((value) => ({
                    key: AuditEntityEnum[value as keyof typeof AuditEntityEnum],
                    value: t(`auditLog:entity.${value}`)
                }))
        },
        {
          name: 'Timestamp',
          description: 'Fecha',
          type: FilterType.DATE
        }
    ]

    const handleClearSearchText = () => {
        setSearchTerm('');
        dispatch(setSearchText({ table: "auditLogs", text: '', goFirstPage: true }))
    }
        
    const onConfirmExportCsv = async () => {
        setLoading(true);

        try {
                const paginationFilters: PaginationFilterDto = 
                {
                    pageNumber: pagination.currentPage,
                    pageSize: pagination.itemsPerPage,
                    search: pagination.searchText,
                    filters: selectedFilters,
                    selectedOrganizationalStructureIds: []
                };
            await exportAuditLogsToCSV(paginationFilters);

            setShowCsvModal(false);
            showAlert({
                message: t("auditLog:exportCsvSuccess"),
                type: "success"
            });
        } catch (e) {
            setErrorModal({ errorModal: t("common:requestError.ErrorBadRequest") });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{t("auditLog:titleAuditLog")}</h2>
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
                <Col className="text-center" xs={12} md={12}>
                    {messageError && <div className="alert alert-danger">{messageError}
                        <button
                            type="button"
                            className="btn-close btn-sm position-absolute top-0 end-0 m-2"
                            aria-label="Close"
                            onClick={() => setMessageError(undefined)}
                        ></button>
                    </div>}
                </Col>
            </Row>
            
            <Row className="mb-2">
                <Col xs={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <Search
                                searchText={searchTerm || ''}
                                onChange={setSearchTerm}
                                onSearch={() => dispatch(setSearchText({ table: "auditLogs", text: searchTerm || '', goFirstPage: true }))}
                                onClear={handleClearSearchText}
                            />
                        </div>
                        <Button variant="" onClick={() => setIsFilterVisible(!isFilterVisible)} aria-controls="collapse-filter-id" style={{ border: 0, padding: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '40px' }}
                            />
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                <ModalGlobal<UseSelectBasicObject>
                    show={showCsvModal}
                    onClose={() => setShowCsvModal(false)}
                    title={t("common:pagination.linkExportCSV")}
                    contentType={"exportCsv"}
                    subtitle={t("participant:modal.participantModalHeaderExport1")}
                    saveText={t("common:modalActionOK")}
                    closeText={t("common:modalActionNOTOK")}
                    error={errorModal?.endDate || errorModal?.errorModal}
                    onSave={() => onConfirmExportCsv()}
                    isLoading={isLoading}
                    showOptions = {true}
                />
            </Row>  
            {filters &&
                <FiltersContainer
                    filters={filters}
                    isFilterVisible={isFilterVisible}
                    tableKey="auditLogs"
                    historicalKey="auditLogs"
                />
            }
            {isLoadingData ?
                <div className="align-content-center" style={{minHeight: 620} }>
                    <SpinerLoader />
                </div>
                : <AppTable<AuditLogListItem>
                    data={tableData?.items ?? []}
                    columns={columns}
                    emptyMessage={t("auditLog:nonAuditLogs")}
                    t={t}
                    hasActions={true}
                    hasPagination={true}
                    currentPage={tableData?.page ?? 0}
                    totalPages={tableData?.totalPages ?? 0}
                    pageSize={tableData?.size ?? 0}
                    paginationType="auditLogs"
                    onExportCsv={() => {setShowCsvModal(true)}}
                    sectionPermission={SectionPermissionTypeEnum.AUDIT}
                />
            }        
        </>
    );
}
